import { Injectable } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, of, timer } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

/**
 * Selective, deferred route preloading strategy.
 *
 * Only routes explicitly opted in with `data: { preload: true }` are preloaded;
 * every other lazy route stays fully on-demand (true lazy loading). Preloading
 * of flagged routes is also delayed by {@link PRELOAD_DELAY_MS} so it never
 * competes with the initial render or the wallet/Web3 bootstrap on the login
 * screen.
 *
 * This replaces `PreloadAllModules`, which eagerly fetched every route chunk
 * (including the heavy admin pages) right after startup even if the user never
 * navigated to them.
 */
@Injectable({ providedIn: 'root' })
export class SelectivePreloadStrategy implements PreloadingStrategy {
  /** How long to wait after startup before preloading flagged routes. */
  private static readonly PRELOAD_DELAY_MS = 2000;

  preload(route: Route, load: () => Observable<unknown>): Observable<unknown> {
    if (route.data?.['preload'] !== true) {
      return of(null);
    }
    return timer(SelectivePreloadStrategy.PRELOAD_DELAY_MS).pipe(mergeMap(() => load()));
  }
}
